import { ActionIcon, Group, Modal, TextInput, Text, Loader, Flex, Button, Checkbox } from "@mantine/core"
import { useContext, useEffect, useState, useCallback } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { NBAInsightsContext } from "../context/global-state"
import PlaylistForm from "../components/PlaylistForm/PlaylistForm"
import { useAuth0 } from "@auth0/auth0-react"
import { addVideoToPlaylist, createPlaylist, downloadPlaylist, getUserPlaylists, updatePlaylist } from "../api/Playlists"
import { notifications } from "@mantine/notifications"
import { Playlist, PlaylistParams } from "../client"
import { IconCheck, IconCopy } from "@tabler/icons-react"


const Modals = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [copied, setCopied] = useState<boolean>(false)
  const [selectedPlaylists, setSelectedPlaylists] = useState<Playlist[]>([])
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0()
  const { userPlaylists, setUserPlaylists } = useContext(NBAInsightsContext)
  const { modalId, playlistId } = useParams()

  useEffect(() => {
    if (modalId === "add-to-playlist") {
      const fetchUserData = async () => {
        if (isAuthenticated && user) {
          const accessToken = await getAccessTokenSilently()
          const playlistList = await getUserPlaylists(accessToken)
          if (playlistList) {
            setUserPlaylists(playlistList)
            const playlistVideoParams = location.state?.playlistVideoParams
            const currentPlaylists = playlistList.filter(p => p.videos.findIndex(v => playlistVideoParams?.id ? v.id === playlistVideoParams.id : `${v.gameId}-${v.eventNumber}` === `${playlistVideoParams?.gameId}-${playlistVideoParams?.eventNumber}`) !== -1 )
            setSelectedPlaylists(currentPlaylists)
          }
          setLoading(false)
        }

      }

      setLoading(true)
      fetchUserData()
    }
  }, [user, getAccessTokenSilently, isAuthenticated, modalId, setUserPlaylists, location.state])

  // Create playlist
  const handleCreatePlaylist = async (data: PlaylistParams) => {
    if (userPlaylists.find(v => v.name === data.name)) {
      notifications.show({
        message: `Playlist '${data.name}' already exists`
      })
      return
    }

    const accessToken = await getAccessTokenSilently()
    const playlist = await createPlaylist({
      name: data.name,
      description: data.description,
      private: data.private,
      thumbnailUrl: data.thumbnailUrl
    }, accessToken)

    if (playlist) {
      setUserPlaylists(prev => [...prev, playlist])
    }
    navigate(location.state.previousLocation)
  }

  const handleUpdatePlaylist = async (data: PlaylistParams) => {
    if (!playlistId) return

    const accessToken = await getAccessTokenSilently()
    const playlist = await updatePlaylist(
      playlistId,
      data.name,
      data.description,
      data.private,
      data.thumbnailUrl || "",
      accessToken
    )
    if (playlist) {
      setUserPlaylists(prev => [...prev.filter(p => p.id !== playlist.id), playlist])
    }
    navigate(location.state.previousLocation)
  }

  const handleAddToPlaylist = async (playlists: Playlist[]) => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: window.env.AUTH_AUDIENCE === "__AUTH_AUDIENCE__" ? process.env.REACT_APP_AUTH_AUDIENCE : window.env.AUTH_AUDIENCE
      }
    });

    
    playlists.forEach(async (playlist) => {
      const playlistVideo = await addVideoToPlaylist(location.state.playlistVideoParams, playlist.id, accessToken)
      notifications.show({
        message: playlistVideo ? `Added video to '${playlist.name}' playlist` : `Video already exists in playlist '${playlist.name}'`
      })
    })

    handleCloseModal()
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(location.state.url);
    setCopied(true);
  }

  const handleCloseModal = useCallback(() => {
    navigate(location.state.previousLocation)
  }, [location.state.previousLocation, navigate])

  const handleDownloadPlaylist = useCallback(async () => {
    if (!playlistId) {
      notifications.show({
        message: "Playlist not found"
      })
      return handleCloseModal()
    }

    const token = await getAccessTokenSilently()
    const downloadPromise = downloadPlaylist(playlistId, token)
    downloadPromise.finally(() => {
      handleCloseModal()
    })
  }, [getAccessTokenSilently, handleCloseModal, playlistId])

  useEffect(() => {
    if (modalId === "download") {
      handleDownloadPlaylist()
    }
  }, [handleDownloadPlaylist, modalId])
  
  switch (modalId) {
    case "playlist":
      return (
        <Modal title="Create Playlist" styles={{ content: { borderRadius: 30, padding: 5 }, title: { fontSize: 20, fontWeight: 500 } }} opened={true} onClose={handleCloseModal} withCloseButton={true}>
          <PlaylistForm title="" submitText="Create" playlist={null} onSubmit={handleCreatePlaylist} />
        </Modal>
      )

    case "edit":
      return (
        <Modal title="Edit Playlist" styles={{ content: { borderRadius: 30, padding: 5 }, title: { fontSize: 20, fontWeight: 500 } }} opened={true} onClose={handleCloseModal} withCloseButton={true}>
          <PlaylistForm title="" submitText="Update" playlist={userPlaylists.find(p => p.id === playlistId) || null} onSubmit={handleUpdatePlaylist}/>
        </Modal>
      )
    
    case "add-to-playlist":
      return (
        <Modal title="Add to Playlists" styles={{ content: { borderRadius: 30, padding: 5 }, title: { fontSize: 20, fontWeight: 500 } }} opened={true} onClose={handleCloseModal} withCloseButton={true}>
          { !loading  ?
            <Flex direction={"column"} w={"100%"} m={10} align={"space-between"}>
                { userPlaylists.map(playlist => {
                  return <Checkbox
                    key={playlist.id}
                    label={playlist.name}
                    w={"100%"}
                    mt={10}
                    mb={20}
                    onChange={(e) => {
                      e.currentTarget.checked
                        ? setSelectedPlaylists(prev => [...prev, playlist])
                        : setSelectedPlaylists(prev => prev.filter(p => p.id !== playlist.id))
                    }}
                  />
                })}
                <Button onClick={() => handleAddToPlaylist(selectedPlaylists)}>
                  Add to playlists
                </Button>
            </Flex>
          :
            <Loader /> 
          }
        </Modal>
      )

    case "share":
      return (
        <Modal title="Share Playlist" styles={{ content: { borderRadius: 30, padding: 5 } }} opened={true} onClose={handleCloseModal} withCloseButton={true}>
          <Group gap="xs">
            <TextInput
              value={location.state.url}
              readOnly
              style={{ flex: 1 }}
              variant="filled" />
            <ActionIcon
              variant="filled"
              color={copied ? 'green' : 'blue'}
              onClick={handleCopy}
              title="Copy URL"
            >
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Group>
        </Modal>
      )
    case "download":
      return(
        <Modal opened={true} onClose={handleCloseModal} withCloseButton={false}>
          <Text fz={"h3"} ta={"center"} fw={600}>Downloading...</Text>
        </Modal>
      )
    default:
      return <></>
  }
}

export default Modals